import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EffectsBase } from '@ps/core-data-access';
import { ToastService } from '@ps/core-overlay';
import { ManageSurveyActions } from '@ps/survey-data-access';
import { map, of, switchMap, tap } from 'rxjs';

import {
  SurveyManagementActions,
  SurveyManagementApiActions,
} from './survey-management.actions';
import { SurveyManagementService } from '../services/survey-management.service';

@Injectable()
export class SurveyManagementEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _service = inject(SurveyManagementService);
  private readonly _toast = inject(ToastService);

  public assignBuyerProjectManager$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyManagementActions.assignBuyerProjectManager),
      switchMap(({ surveyId, projectManager }) =>
        this._service
          .assignBuyerProjectManager(surveyId, projectManager.id)
          .pipe(
            tap(() =>
              this._toast.success(
                'Success! Buyer Project Manager assigned successfully',
                { keepOpen: false },
              ),
            ),
            map(() =>
              SurveyManagementApiActions.assignBuyerProjectManagerSuccess({
                surveyId,
                projectManager,
              }),
            ),
            this.psCatchError(
              (error) =>
                of(
                  SurveyManagementApiActions.assignBuyerProjectManagerFailure({
                    error: error.message,
                  }),
                ),
              { notify: true },
            ),
          ),
      ),
    ),
  );

  public removeBuyerProjectManager$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyManagementActions.removeBuyerProjectManager),
      switchMap(({ surveyId }) =>
        this._service.removeBuyerProjectManager(surveyId).pipe(
          tap(() =>
            this._toast.success(
              'Success! Buyer Project Manager removed successfully',
              { keepOpen: false },
            ),
          ),
          map(() =>
            SurveyManagementApiActions.removeBuyerProjectManagerSuccess({
              surveyId,
            }),
          ),
          this.psCatchError(
            (error) =>
              of(
                SurveyManagementApiActions.removeBuyerProjectManagerFailure({
                  error: error.message,
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public assignOperatorProjectManager$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyManagementActions.assignOperatorProjectManager),
      switchMap(({ surveyId, projectManager }) =>
        this._service
          .assignOperatorProjectManager(surveyId, projectManager.id)
          .pipe(
            tap(() =>
              this._toast.success(
                'Success! Project Manager assigned successfully',
                { keepOpen: false },
              ),
            ),
            map(() =>
              SurveyManagementApiActions.assignOperatorProjectManagerSuccess({
                surveyId,
                projectManager,
              }),
            ),
            this.psCatchError(
              (error) =>
                of(
                  SurveyManagementApiActions.assignOperatorProjectManagerFailure(
                    {
                      error: error.message,
                    },
                  ),
                ),
              { notify: true },
            ),
          ),
      ),
    ),
  );

  public removeOperatorProjectManager$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyManagementActions.removeOperatorProjectManager),
      switchMap(({ surveyId }) =>
        this._service.removeOperatorProjectManager(surveyId).pipe(
          tap(() =>
            this._toast.success(
              'Success! Project Manager removed successfully',
              { keepOpen: false },
            ),
          ),
          map(() =>
            SurveyManagementApiActions.removeOperatorProjectManagerSuccess({
              surveyId,
            }),
          ),
          this.psCatchError(
            (error) =>
              of(
                SurveyManagementApiActions.removeOperatorProjectManagerFailure({
                  error: error.message,
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public patchSurveyOnBuyerProjectManagerAssigned$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyManagementApiActions.assignBuyerProjectManagerSuccess),
      map(({ surveyId, projectManager }) =>
        ManageSurveyActions.updateSurvey({
          surveyId,
          survey: { buyerPm: projectManager.name },
        }),
      ),
    ),
  );

  public patchSurveyOnBuyerProjectManagerRemoved$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyManagementApiActions.removeBuyerProjectManagerSuccess),
      map(({ surveyId }) =>
        ManageSurveyActions.updateSurvey({ surveyId, survey: { buyerPm: '' } }),
      ),
    ),
  );

  public patchSurveyOnOperatorProjectManagerAssigned$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyManagementApiActions.assignOperatorProjectManagerSuccess),
      map(({ surveyId, projectManager }) =>
        ManageSurveyActions.updateSurvey({
          surveyId,
          survey: { operatorPm: projectManager.name },
        }),
      ),
    ),
  );

  public patchSurveyOnOperatorProjectManagerRemoved$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyManagementApiActions.removeOperatorProjectManagerSuccess),
      map(({ surveyId }) =>
        ManageSurveyActions.updateSurvey({
          surveyId,
          survey: { operatorPm: '' },
        }),
      ),
    ),
  );
}
